type PdfOrientation = "portrait" | "landscape";

interface PdfExportDocument {
  element: HTMLDivElement;
  orientation: PdfOrientation;
}

const BRAND = {
  primary900: "#0f2b2a",
  primary800: "#184342",
  primary700: "#205b5a",
  primary100: "#e8f4f3",
  pageCanvas: "#ffffff",
  warmCanvas: "#faf5ef",
  warmSurface: "#fffdfb",
  divider: "#ecddcc",
  muted: "#715f57",
};

const style = (
  element: HTMLElement,
  properties: Partial<CSSStyleDeclaration>
): void => {
  Object.assign(element.style, properties);
};

const getColumnCount = (table: HTMLTableElement): number =>
  Math.max(0, ...Array.from(table.rows).map((row) => row.cells.length));

const getOrientation = (table: HTMLTableElement): PdfOrientation =>
  getColumnCount(table) > 5 ? "landscape" : "portrait";

const looksNumeric = (value: string): boolean =>
  /^\s*[-+]?\s*(?:[$€£﷼]\s*)?\d[\d,.]*(?:\s*(?:%|mm|cm|mL|mg|g|kg|yr|yrs|year|years))?\s*$/i.test(
    value
  );

const getNumericColumns = (table: HTMLTableElement): Set<number> => {
  const bodyRows = Array.from(table.tBodies).flatMap((body) =>
    Array.from(body.rows)
  );
  const numericColumns = new Set<number>();

  for (let columnIndex = 0; columnIndex < getColumnCount(table); columnIndex += 1) {
    const values = bodyRows
      .map((row) => row.cells[columnIndex]?.textContent?.trim() || "")
      .filter(Boolean);

    if (values.length > 0 && values.every(looksNumeric)) {
      numericColumns.add(columnIndex);
    }
  }

  return numericColumns;
};

const formatGeneratedDate = (): string =>
  new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date());

export const createPdfExportDocument = (
  sourceTable: HTMLTableElement,
  logoUrl?: string
): PdfExportDocument => {
  const orientation = getOrientation(sourceTable);
  const columnCount = getColumnCount(sourceTable);
  const numericColumns = getNumericColumns(sourceTable);
  const contentWidth = orientation === "landscape" ? "269mm" : "182mm";
  const cellFontSize = columnCount > 7 ? "8px" : columnCount > 5 ? "9px" : "10px";

  const documentElement = document.createElement("div");
  documentElement.dataset.pdfExportDocument = "true";
  style(documentElement, {
    width: contentWidth,
    boxSizing: "border-box",
    padding: "0",
    background: BRAND.pageCanvas,
    color: BRAND.primary900,
    fontFamily: '"Raleway", "Noto Sans Arabic", Arial, sans-serif',
    lineHeight: "1.45",
  });

  const header = document.createElement("header");
  style(header, {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "20px",
    marginBottom: "14px",
    paddingBottom: "13px",
    borderBottom: `1px solid ${BRAND.divider}`,
  });

  const brandGroup = document.createElement("div");
  style(brandGroup, {
    display: "flex",
    alignItems: "center",
  });

  if (logoUrl) {
    const logo = document.createElement("img");
    logo.src = logoUrl;
    logo.alt = "C-Shaped";
    style(logo, {
      display: "block",
      width: "116px",
      height: "auto",
      objectFit: "contain",
    });
    brandGroup.appendChild(logo);
  } else {
    const brandName = document.createElement("strong");
    brandName.textContent = "C-Shaped";
    style(brandName, {
      color: BRAND.primary800,
      fontSize: "18px",
      fontWeight: "800",
    });
    brandGroup.appendChild(brandName);
  }

  const meta = document.createElement("div");
  meta.textContent = formatGeneratedDate();
  style(meta, {
    flex: "0 0 auto",
    color: BRAND.muted,
    fontSize: "9px",
    fontWeight: "600",
    textAlign: "right",
  });

  header.append(brandGroup, meta);
  documentElement.appendChild(header);

  const tableFrame = document.createElement("div");
  style(tableFrame, {
    overflow: "hidden",
    border: `1px solid ${BRAND.divider}`,
    borderRadius: "8px",
    background: BRAND.warmSurface,
  });

  const table = sourceTable.cloneNode(true) as HTMLTableElement;
  table.removeAttribute("class");
  style(table, {
    width: "100%",
    borderCollapse: "collapse",
    borderSpacing: "0",
    tableLayout: "fixed",
    color: BRAND.primary900,
    fontSize: cellFontSize,
  });

  Array.from(table.rows).forEach((row, rowIndex) => {
    style(row, {
      breakInside: "avoid",
      pageBreakInside: "avoid",
      background: rowIndex % 2 === 0 ? BRAND.warmSurface : BRAND.warmCanvas,
    });

    Array.from(row.cells).forEach((cell, columnIndex) => {
      const isHeader = cell.tagName === "TH";
      cell.removeAttribute("class");
      cell.setAttribute("dir", "auto");
      style(cell, {
        boxSizing: "border-box",
        padding: columnCount > 6 ? "7px 6px" : "8px 9px",
        borderRight:
          columnIndex < row.cells.length - 1
            ? `1px solid ${BRAND.divider}`
            : "none",
        borderBottom: `1px solid ${BRAND.divider}`,
        background: isHeader ? BRAND.primary100 : "transparent",
        color: isHeader ? BRAND.primary700 : BRAND.primary900,
        fontWeight: isHeader ? "800" : "500",
        lineHeight: "1.45",
        overflowWrap: "anywhere",
        textAlign: numericColumns.has(columnIndex) ? "right" : "start",
        verticalAlign: "top",
        wordBreak: "break-word",
      });

      if (numericColumns.has(columnIndex)) {
        cell.style.fontVariantNumeric = "tabular-nums";
      }
    });
  });

  const lastRow = table.rows[table.rows.length - 1];
  if (lastRow) {
    Array.from(lastRow.cells).forEach((cell) => {
      cell.style.borderBottom = "none";
    });
  }

  tableFrame.appendChild(table);
  documentElement.appendChild(tableFrame);

  const footer = document.createElement("footer");
  footer.textContent = "Generated with C-Shaped AI • c-shaped.com";
  style(footer, {
    marginTop: "12px",
    paddingTop: "9px",
    borderTop: `1px solid ${BRAND.divider}`,
    color: BRAND.muted,
    fontSize: "8px",
    fontWeight: "600",
    textAlign: "center",
  });
  documentElement.appendChild(footer);

  return { element: documentElement, orientation };
};

const waitForImages = async (element: HTMLElement): Promise<void> => {
  await Promise.all(
    Array.from(element.querySelectorAll("img")).map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          image.addEventListener("load", () => resolve(), { once: true });
          image.addEventListener("error", () => resolve(), { once: true });
        })
    )
  );
};

const createFilename = (): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `c-shaped-ai-table-${timestamp}.pdf`;
};

export const exportTableAsPdf = async (
  table: HTMLTableElement,
  logoUrl?: string
): Promise<void> => {
  const { element, orientation } = createPdfExportDocument(table, logoUrl);
  const renderHost = document.createElement("div");
  style(renderHost, {
    position: "absolute",
    top: "0",
    left: "-10000px",
    pointerEvents: "none",
  });
  renderHost.appendChild(element);
  document.body.appendChild(renderHost);

  try {
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    await waitForImages(element);

    const { default: html2pdf } = await import(
      "html2pdf.js/dist/html2pdf.bundle.min.js"
    );
    await html2pdf()
      .set({
        margin: [12, 14, 14, 14],
        filename: createFilename(),
        image: { type: "jpeg", quality: 0.98 },
        enableLinks: false,
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: BRAND.pageCanvas,
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation,
        },
        pagebreak: {
          mode: ["css", "legacy"],
          avoid: ["tr"],
        },
      } as any)
      .from(element)
      .save();
  } finally {
    renderHost.remove();
  }
};
